import Loader from 'components/Loader';
import SearchBar from 'components/tables/list-table/filter-pieces/SearchBar';
import { useCallback, useState } from 'react';
import { useDebouncedCallback } from 'hooks/useDebouncedCallback';
import { useGetMattersStatesQuery } from 'store/api/matters';
import { FeatureFlag } from 'types/features';
import { t } from 'utils/getTranslations';
import { useHasPermissions } from 'utils/permissionUtil';
import { buildRsqlFromSearchText } from 'utils/searchQueryToRsql';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags';
import { MATTER_SEARCH_BAR_SELECTORS } from './MattersAccordionTable';
import MattersTableGrouped from './MattersTableGrouped';

interface Props {
    /** Not read: colours come from the tokens. */
    theme?: unknown;
    contactIdList: number[];
    enableSequenceNumberColumn?: boolean;
}

const MattersTableContactView: React.FC<Props> = ({ contactIdList, enableSequenceNumberColumn = false }) => {
    const [searchBarState, setSearchBarState] = useState('');
    const [searchText, setSearchText] = useState('');
    const [searchFilter, setSearchFilter] = useState<string>();

    const isSequenceNumberFeatureEnabled = useIsFeatureEnabled(FeatureFlag.CHANGE_MATTER_SEQUENCE_NUMBER);
    const hasSequenceNumberPermission = useHasPermissions(['UPDATE.MATTERS.CHANGE_MATTER_SEQUENCE_NUMBER']);
    const showSequenceNumberColumn =
        enableSequenceNumberColumn && isSequenceNumberFeatureEnabled && hasSequenceNumberPermission;

    const { data: matterStateCount, isLoading: isMatterStateCountLoading } = useGetMattersStatesQuery({
        clientIds: contactIdList.map((id) => Number(id)),
        filter: searchFilter,
    });

    const handleSearchBarChange = useCallback((value = '') => {
        setSearchBarState(value);
    }, []);

    const handleSearch = useCallback((searchText: string) => {
        setSearchText(searchText);
        // Built here, not in render: building the search warns when keywords were dropped.
        setSearchFilter(buildRsqlFromSearchText(searchText, MATTER_SEARCH_BAR_SELECTORS) || undefined);
    }, []);

    const handleSearchDebounced = useDebouncedCallback((value = '') => {
        handleSearch(value);
    }, 600);

    if (isMatterStateCountLoading || !matterStateCount) {
        return <Loader />;
    }

    return (
        <>
            <div className="my-6">
                <SearchBar
                    value={searchBarState}
                    onChange={(event) => {
                        const value = event.target.value;
                        handleSearchBarChange(value);
                        handleSearchDebounced(value);
                    }}
                    pluralTextId="matters.matters"
                    singularTextId="matters.matter"
                    placeHolder={t('matters.search-cases')}
                />
            </div>
            <div className="flex flex-col gap-10">
                <MattersTableGrouped
                    group="connected"
                    matterStateCount={matterStateCount}
                    groupTitle="contacts.connected-cases"
                    contactIdList={contactIdList}
                    searchText={searchText}
                    disableUrlState
                    showSequenceNumberColumn={showSequenceNumberColumn}
                />
                <MattersTableGrouped
                    group="opponent"
                    matterStateCount={matterStateCount}
                    groupTitle="contacts.opponent-in-cases"
                    contactIdList={contactIdList}
                    searchText={searchText}
                    disableUrlState
                    showSequenceNumberColumn={showSequenceNumberColumn}
                />
                <MattersTableGrouped
                    group="other"
                    matterStateCount={matterStateCount}
                    groupTitle="contacts.other-connected-cases"
                    contactIdList={contactIdList}
                    searchText={searchText}
                    disableUrlState
                    showSequenceNumberColumn={showSequenceNumberColumn}
                />
                <MattersTableGrouped
                    group="payer"
                    matterStateCount={matterStateCount}
                    groupTitle="contacts.payer-in-cases"
                    contactIdList={contactIdList}
                    searchText={searchText}
                    disableUrlState
                    showSequenceNumberColumn={showSequenceNumberColumn}
                />
                <MattersTableGrouped
                    group="primaryContact"
                    matterStateCount={matterStateCount}
                    groupTitle="contacts.primary-contact-in-case"
                    contactIdList={contactIdList}
                    searchText={searchText}
                    disableUrlState
                    showSequenceNumberColumn={showSequenceNumberColumn}
                />
                <MattersTableGrouped
                    group="judge"
                    matterStateCount={matterStateCount}
                    groupTitle="contacts.judge-in-case"
                    contactIdList={contactIdList}
                    searchText={searchText}
                    disableUrlState
                    showSequenceNumberColumn={showSequenceNumberColumn}
                />
            </div>
        </>
    );
};

export default MattersTableContactView;
